import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import logo from "@/assets/reelistiq-wordmark.svg";
import logoWhite from "@/assets/reelistiq-wordmark-white.svg";
import symbol from "@/assets/reelistiq-symbol-black.svg";
import symbolWhite from "@/assets/reelistiq-symbol-white.svg";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X } from "lucide-react";

export function Header() {
  const [isVisible, setIsVisible] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOverHero, setIsOverHero] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mouseMovedRef = useRef(false);
  const { t, localizedPath } = useLanguage();
  const location = useLocation();

  const navItems = [
    { key: "nav.work", href: "/work" },
    { key: "nav.offer", href: "/offer" },
    { key: "nav.team", href: "/team" },
    { key: "nav.faq", href: "/faq" },
    { key: "nav.blog", href: "/blog" },
    { key: "nav.presse", href: "/presse" },
    { key: "nav.contact", href: "/contact" },
  ];

  // Detect if hero section is behind the header
  useEffect(() => {
    const heroEl = document.getElementById("hero-section");
    if (!heroEl) {
      setIsOverHero(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsOverHero(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "0px 0px -90% 0px" }
    );
    observer.observe(heroEl);
    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    const isTouch =
      typeof window !== "undefined" &&
      window.matchMedia("(hover: none), (pointer: coarse)").matches;

    const handleScroll = () => {
      if (isMobileMenuOpen) return;
      setIsScrolling(true);
      setIsVisible(false);
      mouseMovedRef.current = false;
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        // Touch devices emit no mousemove — always restore the header after scrolling stops
        if (mouseMovedRef.current || isTouch) setIsVisible(true);
      }, 150);
    };


    const handleMouseMove = () => {
      mouseMovedRef.current = true;
      if (!isScrolling) setIsVisible(true);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [isScrolling, isMobileMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobileMenuOpen]);

  const light = isOverHero && !isMobileMenuOpen;

  return (
    <>
      <header
        role="banner"
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out",
          isVisible || isMobileMenuOpen
            ? "translate-y-0 opacity-100"
            : "-translate-y-4 opacity-0 pointer-events-none"
        )}
      >
        <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo left */}
          <Link to={localizedPath("/")} className="flex-shrink-0 flex items-center gap-2">
            <img src={light ? symbolWhite : symbol} alt="" title="reelistiq Symbol" width="32" height="32" className="h-8 w-8" />
            <img src={light ? logoWhite : logo} alt="reelistiq" title="reelistiq Wordmark" width="120" height="32" className="h-8" />
          </Link>

          {/* Center nav — hidden on mobile */}
          <nav role="navigation" aria-label="Main navigation" className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={localizedPath(item.href)}
                title={t(item.key)}
                className={cn(
                  "text-xs uppercase tracking-widest transition-colors duration-300 font-normal",
                  light
                    ? "text-[#F9F4EF] hover:text-white"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          {/* Right side: language toggle + CTA + mobile burger */}
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <Link
              to={localizedPath("/contact")}
              title={t("nav.contact")}
              className="hidden md:inline-flex items-center px-5 py-2 rounded-full bg-accent text-accent-foreground text-xs font-medium uppercase tracking-wider transition-opacity hover:opacity-90"
            >
              {t("nav.contact")}
            </Link>
            <button
              className="md:hidden p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen
                ? <X className="w-5 h-5" />
                : <Menu className={cn("w-5 h-5", light && "text-[#F9F4EF]")} />
              }
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-background flex flex-col items-center justify-center gap-8 transition-all duration-300 md:hidden",
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
      >
        {navItems.map((item) => (
          <Link
            key={item.key}
            to={localizedPath(item.href)}
            onClick={() => setIsMobileMenuOpen(false)}
            className="text-2xl font-medium tracking-tight text-foreground hover:text-muted-foreground transition-colors"
          >
            {t(item.key)}
          </Link>
        ))}
        <Link
          to={localizedPath("/contact")}
          onClick={() => setIsMobileMenuOpen(false)}
          className="mt-4 inline-flex items-center px-6 py-3 rounded-full bg-accent text-accent-foreground text-sm font-medium uppercase tracking-wider"
        >
          {t("nav.contact")}
        </Link>
      </div>
    </>
  );
}
